import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Button,
    FormGroup,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Pagination,
    PaginationItem,
    PaginationLink,
    CardFooter,
} from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import classnames from "classnames";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import {
    getAllCountries,
    addCountry,
    updateCountry,
    deleteCountry,
    toggleCountryStatus,
    getAllCities,
    addCity,
    updateCity,
    deleteCity,
    toggleCityStatus,
    getAllStates,
    addState,
    updateState,
    deleteState,
} from "../../api/ApiServices";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
    },
    navWrapper: {
        background: "rgba(247, 250, 252, 0.5)",
        padding: "15px",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
    },
    navLink: {
        cursor: "pointer",
        borderRadius: "12px",
        padding: "12px 24px",
        fontWeight: "600",
        transition: "all 0.3s ease",
    },
    pagination: {
        padding: "1rem 1.5rem",
        borderTop: "1px solid rgba(0,0,0,0.05)",
        background: "rgba(247, 250, 252, 0.3)",
        borderRadius: "0 0 16px 16px",
    }
};

const selectStyles = {
    control: (base) => ({
        ...base,
        borderRadius: '8px',
        borderColor: '#cad1d7',
        minHeight: '45px',
        boxShadow: 'none',
        '&:hover': {
            borderColor: '#5e72e4'
        }
    }),
    option: (base, state) => ({
        ...base,
        cursor: 'pointer',
        backgroundColor: state.isSelected ? '#5e72e4' : state.isFocused ? '#f6f9fc' : 'white',
        color: state.isSelected ? 'white' : '#32325d',
        padding: '10px 15px'
    })
};

// Styled component or inline styles to hide number input spinners
const noSpinnersStyle = `
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] {
    -moz-appearance: textfield;
  }
`;

const CountryCity = () => {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [editingItem, setEditingItem] = useState(null); // { type, data }
    const [modal, setModal] = useState(false);
    const [modalType, setModalType] = useState(""); // "country", "state", "city"
    const [newLabel, setNewLabel] = useState("");
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("All Status");
    const [activeTab, setActiveTab] = useState("1");
    const [errors, setErrors] = useState({});

    // react-select states
    const [selectedCountryOption, setSelectedCountryOption] = useState(null);
    const [selectedStateOption, setSelectedStateOption] = useState(null);

    // Pagination states
    const [countryPage, setCountryPage] = useState(1);
    const [countryLimit] = useState(10);
    const [countryTotal, setCountryTotal] = useState(0);

    const [statePage, setStatePage] = useState(1);
    const [stateLimit] = useState(10);
    const [stateTotal, setStateTotal] = useState(0);

    const [cityPage, setCityPage] = useState(1);
    const [cityLimit] = useState(10);
    const [cityTotal, setCityTotal] = useState(0);

    // View Cities Modal states
    const [viewCitiesModal, setViewCitiesModal] = useState(false);
    const [selectedViewCountry, setSelectedViewCountry] = useState(null);
    const [countryCities, setCountryCities] = useState([]);
    const [countryCitiesPage, setCountryCitiesPage] = useState(1);
    const [countryCitiesTotal, setCountryCitiesTotal] = useState(0);
    const [viewCitiesLoading, setViewCitiesLoading] = useState(false);

    // View States Modal states
    const [viewStatesModal, setViewStatesModal] = useState(false);
    const [selectedViewStatesCountry, setSelectedViewStatesCountry] = useState(null);
    const [countryStatesData, setCountryStatesData] = useState([]);
    const [countryStatesPage, setCountryStatesPage] = useState(1);
    const [countryStatesTotal, setCountryStatesTotal] = useState(0);
    const [viewStatesLoading, setViewStatesLoading] = useState(false);

    // View State Cities Modal states
    const [viewStateCitiesModal, setViewStateCitiesModal] = useState(false);
    const [selectedViewState, setSelectedViewState] = useState(null);
    const [stateCitiesData, setStateCitiesData] = useState([]);
    const [stateCitiesPage, setStateCitiesPage] = useState(1);
    const [stateCitiesTotal, setStateCitiesTotal] = useState(0);
    const [viewStateCitiesLoading, setViewStateCitiesLoading] = useState(false);

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const toggle = () => {
        setModal(!modal);
        setErrors({});
        if (modal) {
            setEditingItem(null);
            setSelectedCountry(null);
            setSelectedState(null);
            setNewLabel("");
            setLatitude("");
            setLongitude("");
            setSelectedCountryOption(null);
            setSelectedStateOption(null);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const statusParam = filterStatus === "All Status" ? "" : (filterStatus === "Active" ? "1" : "0");

            if (activeTab === "1") {
                const countryRes = await getAllCountries(countryPage, countryLimit, searchTerm, statusParam);
                setCountries(countryRes.payload.countries || []);
                setCountryTotal(countryRes.payload.total || 0);
            } else if (activeTab === "2") {
                const stateRes = await getAllStates(statePage, stateLimit, "", searchTerm);
                setStates(stateRes.payload.states || []);
                setStateTotal(stateRes.payload.total || 0);
            } else {
                const cityRes = await getAllCities(cityPage, cityLimit, "", "", searchTerm, statusParam);
                setCities(cityRes.payload.cities || []);
                setCityTotal(cityRes.payload.total || 0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            Swal.fire("Error", "Failed to load operational data", "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchCountryCities = async (countryUuid, page = 1) => {
        setViewCitiesLoading(true);
        try {
            const res = await getAllCities(page, 10, countryUuid, "", "", "");
            setCountryCities(res.payload.cities || []);
            setCountryCitiesTotal(res.payload.total || 0);
            setCountryCitiesPage(page);
        } catch (error) {
            console.error("Error fetching country cities:", error);
        } finally {
            setViewCitiesLoading(false);
        }
    };

    const fetchCountryStates = async (countryUuid, page = 1) => {
        setViewStatesLoading(true);
        try {
            const res = await getAllStates(page, 10, countryUuid, "");
            setCountryStatesData(res.payload.states || []);
            setCountryStatesTotal(res.payload.total || 0);
            setCountryStatesPage(page);
        } catch (error) {
            console.error("Error fetching country states:", error);
        } finally {
            setViewStatesLoading(false);
        }
    };

    const fetchStateCities = async (stateUuid, page = 1) => {
        setViewStateCitiesLoading(true);
        try {
            const res = await getAllCities(page, 10, "", stateUuid, "", "");
            setStateCitiesData(res.payload.cities || []);
            setStateCitiesTotal(res.payload.total || 0);
            setStateCitiesPage(page);
        } catch (error) {
            console.error("Error fetching state cities:", error);
        } finally {
            setViewStateCitiesLoading(false);
        }
    };

    const loadCountryOptions = async (inputValue) => {
        try {
            const res = await getAllCountries(1, 100, inputValue, "1");
            return res.payload.countries.map(c => ({
                label: c.c_name,
                value: c.c_uuid
            }));
        } catch (error) {
            console.error("Error loading country options:", error);
            return [];
        }
    };

    const loadStateOptions = async (inputValue) => {
        try {
            const countryUuid = selectedCountryOption?.value;
            if (!countryUuid) return [];
            const res = await getAllStates(1, 100, countryUuid, inputValue);
            return res.payload.states.map(s => ({
                label: s.s_name,
                value: s.s_uuid
            }));
        } catch (error) {
            console.error("Error loading state options:", error);
            return [];
        }
    };

    useEffect(() => {
        fetchData();
    }, [searchTerm, filterStatus, countryPage, statePage, cityPage, activeTab]);

    const handleViewCities = (country) => {
        setSelectedViewCountry(country);
        setCountryCitiesPage(1);
        fetchCountryCities(country.c_uuid, 1);
        setViewCitiesModal(true);
    };

    const toggleViewCitiesModal = () => {
        setViewCitiesModal(!viewCitiesModal);
        if (viewCitiesModal) {
            setSelectedViewCountry(null);
            setCountryCities([]);
        }
    };

    const handleViewStates = (country) => {
        setSelectedViewStatesCountry(country);
        setCountryStatesPage(1);
        fetchCountryStates(country.c_uuid, 1);
        setViewStatesModal(true);
    };

    const toggleViewStatesModal = () => {
        setViewStatesModal(!viewStatesModal);
        if (viewStatesModal) {
            setSelectedViewStatesCountry(null);
            setCountryStatesData([]);
        }
    };

    const handleViewStateCities = (state) => {
        setSelectedViewState(state);
        setStateCitiesPage(1);
        fetchStateCities(state.s_uuid, 1);
        setViewStateCitiesModal(true);
    };

    const toggleViewStateCitiesModal = () => {
        setViewStateCitiesModal(!viewStateCitiesModal);
        if (viewStateCitiesModal) {
            setSelectedViewState(null);
            setStateCitiesData([]);
        }
    };

    const handleAddCountry = () => {
        setModalType("country");
        setEditingItem(null);
        setNewLabel("");
        toggle();
    };

    const handleEditCountry = (country) => {
        setModalType("country");
        setEditingItem({ type: "country", data: country });
        setNewLabel(country.c_name);
        setLatitude(country.latitude || "");
        setLongitude(country.longitude || "");
        toggle();
    };

    const handleAddState = (country = null) => {
        if (country) {
            setSelectedCountryOption({ label: country.c_name, value: country.c_uuid });
        } else {
            setSelectedCountryOption(null);
        }
        setModalType("state");
        setEditingItem(null);
        setNewLabel("");
        toggle();
    };

    const handleEditState = (state) => {
        setSelectedCountryOption({ label: state.c_name || "Unknown Country", value: state.c_uuid });
        setModalType("state");
        setEditingItem({ type: "state", data: state });
        setNewLabel(state.s_name);
        toggle();
    };

    const handleAddCity = (state = null) => {
        if (state) {
            setSelectedCountryOption({ label: state.c_name, value: state.c_uuid });
            setSelectedStateOption({ label: state.s_name, value: state.s_uuid });
        } else {
            setSelectedCountryOption(null);
            setSelectedStateOption(null);
        }
        setModalType("city");
        setEditingItem(null);
        setNewLabel("");
        toggle();
    };

    const handleEditCity = (city) => {
        setSelectedCountryOption({ label: city.c_name || "Unknown Country", value: city.c_uuid });
        setSelectedStateOption({ label: city.s_name || "Unknown State", value: city.s_uuid });

        setModalType("city");
        setEditingItem({ type: "city", data: city });
        setNewLabel(city.ci_name);
        toggle();
    };

    const handleDeleteCountry = async (id) => {
        const result = await Swal.fire({
            title: "Remove Country?",
            text: "All associated cities and services will be affected.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete"
        });

        if (result.isConfirmed) {
            try {
                await deleteCountry(id);
                Swal.fire("Removed!", "Operational region deleted.", "success");
                fetchData();
            } catch (error) {
                Swal.fire("Error", error.response?.data?.message || "Failed to delete country", "error");
            }
        }
    };

    const handleDeleteState = async (id) => {
        const result = await Swal.fire({
            title: "Delete State?",
            text: "Cities in this state must be deleted first.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete"
        });

        if (result.isConfirmed) {
            try {
                await deleteState(id);
                fetchData();
                Swal.fire("Deleted!", "State record removed.", "success");
            } catch (error) {
                Swal.fire("Error", error.response?.data?.message || "Failed to delete state", "error");
            }
        }
    };

    const handleDeleteCity = async (id) => {
        const result = await Swal.fire({
            title: "Delete City?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete"
        });

        if (result.isConfirmed) {
            try {
                await deleteCity(id);
                fetchData();
                Swal.fire("Deleted!", "City record removed.", "success");
            } catch (error) {
                Swal.fire("Error", error.response?.data?.message || "Failed to delete city", "error");
            }
        }
    };
    const handleToggleStatus = async (type, id) => {
        try {
            if (type === "country") {
                await toggleCountryStatus(id);
            } else {
                await toggleCityStatus(id);
            }

            fetchData();

            Swal.fire({
                title: "Updated!",
                text: "Status updated successfully.",
                icon: "success",
                showConfirmButton: false,
                timer: 2000
            });

        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "Failed to update status",
                icon: "error",
                showConfirmButton: false,
                timer: 2000
            });
        }
    };

    /* ═══════════════════════════════════════════════════
       SMART PAGINATION RENDERER
       ═══════════════════════════════════════════════════ */
    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPages = Math.ceil(totalItems / limit);
        if (totalPages <= 1) return null;

        const pages = [];
        const delta = 2; // Number of pages to show on either side of current page

        // Always show first page
        pages.push(
            <PaginationItem key={1} className={currentPage === 1 ? "active" : ""}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        // Show start ellipsis if needed
        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        // Show pages around current
        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        // Show end ellipsis if needed
        if (currentPage < totalPages - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        // Always show last page
        if (totalPages > 1) {
            pages.push(
                <PaginationItem key={totalPages} className={currentPage === totalPages ? "active" : ""}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPages); }}>{totalPages}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const validateForm = () => {
        const newErrors = {};
        const textRegex = /^[a-zA-Z\s]+$/;
        const value = newLabel.trim();

        if (modalType === "country") {
            if (!value) newErrors.name = "Country Name is required.";
            else if (value.length < 2 || value.length > 50) newErrors.name = "Country Name must be between 2 and 50 characters.";
            else if (!textRegex.test(value)) newErrors.name = "Special characters and numbers are not allowed.";
        } else if (modalType === "state") {
            if (!selectedCountryOption) newErrors.country = "Please select a country first.";
            if (!value) newErrors.name = "State Name is required.";
            else if (value.length < 2 || value.length > 50) newErrors.name = "State Name must be between 2 and 50 characters.";
            else if (!textRegex.test(value)) newErrors.name = "Special characters and numbers are not allowed.";
        } else if (modalType === "city") {
            if (!selectedStateOption) newErrors.state = "Please select a state first.";
            if (!value) newErrors.name = "City Name is required.";
            else if (value.length < 2 || value.length > 50) newErrors.name = "City Name must be between 2 and 50 characters.";
            else if (!textRegex.test(value)) newErrors.name = "Special characters and numbers are not allowed.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const actionType = editingItem ? "Update" : "Add";
        const itemType = modalType === "country" ? "Country" : "City";

        // Confirmation dialog stays as-is
        const result = await Swal.fire({
            title: `Confirm ${actionType}?`,
            text: `Are you sure you want to ${actionType.toLowerCase()} this ${itemType.toLowerCase()}?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#5e72e4",
            confirmButtonText: `Yes, ${actionType}`,
            cancelButtonText: "Cancel"
        });

        if (!result.isConfirmed) return;

        try {
            if (modalType === "country") {
                if (editingItem) {
                    await updateCountry(editingItem.data.c_uuid, {
                        c_name: newLabel,
                        latitude: latitude,
                        longitude: longitude
                    });

                    Swal.fire({
                        title: "Updated!",
                        text: "Country renamed successfully.",
                        icon: "success",
                        showConfirmButton: false,
                        timer: 2000
                    });

                } else {
                    await addCountry({
                        c_name: newLabel,
                        latitude: latitude,
                        longitude: longitude
                    });

                    Swal.fire({
                        title: "Added!",
                        text: `${newLabel} is now an operational region.`,
                        icon: "success",
                        showConfirmButton: false,
                        timer: 2000
                    });
                }
            } else if (modalType === "state") {
                const targetCountryUuid = selectedCountryOption?.value;
                if (!targetCountryUuid) {
                    Swal.fire({
                        title: "Wait",
                        text: "Please select a country first.",
                        icon: "warning",
                        showConfirmButton: false,
                        timer: 2000
                    });
                    return;
                }
                if (editingItem) {
                    await updateState(editingItem.data.s_uuid, { s_name: newLabel, c_uuid: targetCountryUuid });
                    Swal.fire("Updated!", "State renamed successfully.", "success");
                } else {
                    await addState({ s_name: newLabel, c_uuid: targetCountryUuid });
                    Swal.fire("Added!", `${newLabel} added to the operational region.`, "success");
                }
            } else {
                const targetCountryUuid = selectedCountryOption?.value;
                const targetStateUuid = selectedStateOption?.value;
                if (!targetCountryUuid || !targetStateUuid) {
                    Swal.fire("Wait", "Please select both country and state.", "warning");
                    return;
                }

                if (editingItem) {
                    await updateCity(editingItem.data.ci_uuid, { ci_name: newLabel, c_uuid: targetCountryUuid, s_uuid: targetStateUuid });
                    Swal.fire("Updated!", "City updated successfully.", "success");
                } else {
                    await addCity({ ci_name: newLabel, c_uuid: targetCountryUuid, s_uuid: targetStateUuid });
                    Swal.fire("Added!", `${newLabel} added successfully.`, "success");
                }
            }

            fetchData();
            toggle();

        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "Failed to save record",
                icon: "error",
                showConfirmButton: false,
                timer: 2000
            });
        }
    };

    return (
        <>
            <style>{noSpinnersStyle}</style>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow border-0" style={styles.glassCard}>
                            <CardBody className="p-0">
                                {/* TAB SWITCHER - ROLE MANAGEMENT STYLE */}
                                <div className="nav-wrapper" style={styles.navWrapper}>
                                    <Row className="align-items-center">
                                        <Col md="6">
                                            <Nav className="nav-pills nav-fill flex-column flex-md-row" pills role="tablist">
                                                <NavItem>
                                                    <NavLink
                                                        aria-selected={activeTab === "1"}
                                                        className={classnames("mb-sm-3 mb-md-0", { active: activeTab === "1" })}
                                                        onClick={() => toggleTab("1")}
                                                        href="#pablo"
                                                        style={styles.navLink}
                                                    >
                                                        <i className="ni ni-world-2 mr-2" />
                                                        Countries
                                                    </NavLink>
                                                </NavItem>
                                                <NavItem>
                                                    <NavLink
                                                        aria-selected={activeTab === "2"}
                                                        className={classnames("mb-sm-3 mb-md-0", { active: activeTab === "2" })}
                                                        onClick={() => toggleTab("2")}
                                                        href="#pablo"
                                                        style={styles.navLink}
                                                    >
                                                        <i className="ni ni-map-big mr-2" />
                                                        States
                                                    </NavLink>
                                                </NavItem>
                                                <NavItem>
                                                    <NavLink
                                                        aria-selected={activeTab === "3"}
                                                        className={classnames("mb-sm-3 mb-md-0", { active: activeTab === "3" })}
                                                        onClick={() => toggleTab("3")}
                                                        href="#pablo"
                                                        style={styles.navLink}
                                                    >
                                                        <i className="ni ni-building mr-2" />
                                                        Cities
                                                    </NavLink>
                                                </NavItem>
                                            </Nav>
                                        </Col>
                                        <Col md="6" className="text-right d-none d-md-block">
                                            {activeTab === "1" ? (
                                                <Button color="primary" onClick={handleAddCountry} style={{ borderRadius: "10px" }}>
                                                    <i className="fas fa-plus-circle mr-2" /> Add Country
                                                </Button>
                                            ) : activeTab === "2" ? (
                                                <Button color="primary" onClick={() => handleAddState()} style={{ borderRadius: "10px" }}>
                                                    <i className="fas fa-plus-circle mr-2" /> Add State
                                                </Button>
                                            ) : (
                                                <Button color="primary" onClick={() => handleAddCity()} style={{ borderRadius: "10px" }}>
                                                    <i className="fas fa-plus-circle mr-2" /> Add City
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                </div>

                                {/* SEARCH & FILTERS HEADER */}
                                <CardHeader className="border-0 bg-transparent py-4">
                                    <Row className="align-items-center">
                                        <Col md="4" className="d-md-none mb-3">
                                            {activeTab === "1" ? (
                                                <Button color="primary" onClick={handleAddCountry} block>
                                                    <i className="fas fa-plus-circle mr-2" /> Add Country
                                                </Button>
                                            ) : (
                                                <Button color="primary" onClick={() => handleAddCity()} block>
                                                    <i className="fas fa-plus-circle mr-2" /> Add City
                                                </Button>
                                            )}
                                        </Col>
                                        <Col md="4">
                                            <h3 className="mb-0 text-dark font-weight-700">
                                                <i className="ni ni-map-big text-primary mr-2" /> {activeTab === "1" ? "Operational Countries" : activeTab === "2" ? "Operational States" : "Operational Cities"}
                                            </h3>
                                        </Col>
                                        <Col md="8">
                                            <div className="d-flex justify-content-md-end" style={{ gap: "10px" }}>
                                                <Input
                                                    placeholder={`Search ${activeTab === "1" ? "country" : activeTab === "2" ? "state" : "city"}...`}
                                                    type="text"
                                                    style={{ width: "250px", borderRadius: "8px" }}
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                        setCountryPage(1);
                                                        setStatePage(1);
                                                        setCityPage(1);
                                                    }}
                                                />
                                                <Input
                                                    type="select"
                                                    style={{ width: "150px", borderRadius: "8px" }}
                                                    value={filterStatus}
                                                    onChange={(e) => {
                                                        setFilterStatus(e.target.value);
                                                        setCountryPage(1);
                                                        setStatePage(1);
                                                        setCityPage(1);
                                                    }}
                                                >
                                                    <option>All Status</option>
                                                    <option>Active</option>
                                                    <option>Inactive</option>
                                                </Input>
                                                <Button color="info" outline onClick={fetchData} size="sm" style={{ borderRadius: "8px" }}>
                                                    <i className="fas fa-sync" />
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardHeader>

                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId="1">
                                        <div className="table-responsive">
                                            <Table className="align-items-center table-flush" hover>
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th scope="col" style={{ padding: "16px 24px" }}>Country Name</th>
                                                        <th scope="col">Latitude</th>
                                                        <th scope="col">Longitude</th>
                                                        <th scope="col">Total States</th>
                                                        <th scope="col">Total Cities</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">Added On</th>
                                                        <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loading ? (
                                                        <tr>
                                                            <td colSpan="8" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td>
                                                        </tr>
                                                    ) : countries.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="8" className="text-center py-4 text-muted">No countries found</td>
                                                        </tr>
                                                    ) : (
                                                        countries.map((c) => (
                                                            <tr key={c.c_uuid}>
                                                                <td style={{ padding: "16px 24px" }}><span className="font-weight-bold text-dark">{c.c_name}</span></td>
                                                                <td>{c.latitude || "N/A"}</td>
                                                                <td>{c.longitude || "N/A"}</td>
                                                                <td><Badge color="primary" pill outline>{c.stateCount || 0} States</Badge></td>
                                                                <td><Badge color="info" pill>{c.cityCount || 0} Cities</Badge></td>
                                                                <td>
                                                                    <Badge
                                                                        color={c.c_status === "1" ? "success" : "danger"}
                                                                        pill
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => handleToggleStatus("country", c.c_uuid)}
                                                                    >
                                                                        {c.c_status === "1" ? "Active" : "Inactive"}
                                                                    </Badge>
                                                                </td>
                                                                <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                                                                <td className="text-right" style={{ paddingRight: "24px" }}>
                                                                    <UncontrolledDropdown>
                                                                        <DropdownToggle className="btn-icon-only " role="button" size="sm" color="">
                                                                            <i className="fas fa-ellipsis-v" style={{ color: "#6c757d" }} />
                                                                        </DropdownToggle>
                                                                        <DropdownMenu className="dropdown-menu-arrow" right container="body">
                                                                            <DropdownItem onClick={() => handleViewStates(c)}>
                                                                                <i className="fas fa-map-marked-alt mr-2 text-primary" /> View States
                                                                            </DropdownItem>
                                                                            <DropdownItem onClick={() => handleViewCities(c)}>
                                                                                <i className="fas fa-city mr-2 text-info" /> View Cities
                                                                            </DropdownItem>
                                                                            <DropdownItem divider />
                                                                            <DropdownItem onClick={() => handleEditCountry(c)}>Rename</DropdownItem>
                                                                            <DropdownItem onClick={() => handleToggleStatus("country", c.c_uuid)}>
                                                                                {c.c_status === "1" ? "Deactivate" : "Activate"}
                                                                            </DropdownItem>
                                                                            <DropdownItem onClick={() => handleDeleteCountry(c.c_uuid)} className="text-danger">Delete</DropdownItem>
                                                                        </DropdownMenu>
                                                                    </UncontrolledDropdown>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        {/* Pagination for Countries */}
                                        {!loading && countryTotal > countryLimit && (
                                            <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                                                <span className="text-muted small">Showing {((countryPage - 1) * countryLimit) + 1} to {Math.min(countryPage * countryLimit, countryTotal)} of {countryTotal}</span>
                                                <Pagination className="pagination justify-content-end mb-0">
                                                    <PaginationItem disabled={countryPage <= 1}>
                                                        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setCountryPage(countryPage - 1); }}><i className="fas fa-angle-left" /></PaginationLink>
                                                    </PaginationItem>
                                                    {renderPaginationItems(countryPage, countryTotal, countryLimit, setCountryPage)}
                                                    <PaginationItem disabled={countryPage * countryLimit >= countryTotal}>
                                                        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setCountryPage(countryPage + 1); }}><i className="fas fa-angle-right" /></PaginationLink>
                                                    </PaginationItem>
                                                </Pagination>
                                            </CardFooter>
                                        )}
                                    </TabPane>

                                    <TabPane tabId="2">
                                        <div className="table-responsive">
                                            <Table className="align-items-center table-flush" hover>
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th scope="col" style={{ padding: "16px 24px" }}>State Name</th>
                                                        <th scope="col">Country</th>
                                                        <th scope="col">Total Cities</th>
                                                        <th scope="col">Added On</th>
                                                        <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loading ? (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td>
                                                        </tr>
                                                    ) : states.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-4 text-muted">No states found</td>
                                                        </tr>
                                                    ) : (
                                                        states.map((s) => (
                                                            <tr key={s.s_uuid}>
                                                                <td style={{ padding: "16px 24px" }}><span className="font-weight-bold text-dark">{s.s_name}</span></td>
                                                                <td>{s.c_name || "N/A"}</td>
                                                                <td><Badge color="info" pill>{s.cityCount || 0} Cities</Badge></td>
                                                                <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                                                                <td className="text-right" style={{ paddingRight: "24px" }}>
                                                                    <UncontrolledDropdown>
                                                                        <DropdownToggle className="btn-icon-only " role="button" size="sm" color="">
                                                                            <i className="fas fa-ellipsis-v" style={{ color: "#525f7f" }} />
                                                                        </DropdownToggle>
                                                                        <DropdownMenu className="dropdown-menu-arrow" right container="body">
                                                                            <DropdownItem onClick={() => handleViewStateCities(s)}>
                                                                                <i className="fas fa-city mr-2 text-info" /> View Cities
                                                                            </DropdownItem>
                                                                            <DropdownItem divider />
                                                                            <DropdownItem onClick={() => handleEditState(s)}>Edit State</DropdownItem>
                                                                            <DropdownItem onClick={() => handleDeleteState(s.s_uuid)} className="text-danger">Delete</DropdownItem>
                                                                        </DropdownMenu>
                                                                    </UncontrolledDropdown>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        {/* Pagination for States */}
                                        {!loading && stateTotal > stateLimit && (
                                            <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                                                <span className="text-muted small">Showing {((statePage - 1) * stateLimit) + 1} to {Math.min(statePage * stateLimit, stateTotal)} of {stateTotal}</span>
                                                <Pagination className="pagination justify-content-end mb-0">
                                                    <PaginationItem disabled={statePage <= 1}>
                                                        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setStatePage(statePage - 1); }}><i className="fas fa-angle-left" /></PaginationLink>
                                                    </PaginationItem>
                                                    {renderPaginationItems(statePage, stateTotal, stateLimit, setStatePage)}
                                                    <PaginationItem disabled={statePage * stateLimit >= stateTotal}>
                                                        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setStatePage(statePage + 1); }}><i className="fas fa-angle-right" /></PaginationLink>
                                                    </PaginationItem>
                                                </Pagination>
                                            </CardFooter>
                                        )}
                                    </TabPane>

                                    <TabPane tabId="3">
                                        <div className="table-responsive">
                                            <Table className="align-items-center table-flush" hover>
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th scope="col" style={{ padding: "16px 24px" }}>City Name</th>
                                                        <th scope="col">State</th>
                                                        <th scope="col">Country</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">Added On</th>
                                                        <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loading ? (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td>
                                                        </tr>
                                                    ) : cities.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-4 text-muted">No cities found</td>
                                                        </tr>
                                                    ) : (
                                                        cities.map((city) => (
                                                            <tr key={city.ci_uuid}>
                                                                <td style={{ padding: "16px 24px" }}><span className="font-weight-bold text-dark">{city.ci_name}</span></td>
                                                                <td>{city.s_name || "N/A"}</td>
                                                                <td>{city.c_name || "N/A"}</td>
                                                                <td>
                                                                    <Badge
                                                                        color={city.ci_status === "1" ? "info" : "secondary"}
                                                                        pill
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => handleToggleStatus("city", city.ci_uuid)}
                                                                    >
                                                                        {city.ci_status === "1" ? "Active" : "Inactive"}
                                                                    </Badge>
                                                                </td>
                                                                <td>{new Date(city.createdAt).toLocaleDateString()}</td>
                                                                <td className="text-right" style={{ paddingRight: "24px" }}>
                                                                    <UncontrolledDropdown>
                                                                        <DropdownToggle className="btn-icon-only " role="button" size="sm" color="">
                                                                            <i className="fas fa-ellipsis-v" style={{ color: "#525f7f" }} />
                                                                        </DropdownToggle>
                                                                        <DropdownMenu className="dropdown-menu-arrow" right container="body">
                                                                            <DropdownItem onClick={() => handleEditCity(city)}>Edit City</DropdownItem>
                                                                            <DropdownItem onClick={() => handleToggleStatus("city", city.ci_uuid)}>
                                                                                {city.ci_status === "1" ? "Deactivate" : "Activate"}
                                                                            </DropdownItem>
                                                                            <DropdownItem onClick={() => handleDeleteCity(city.ci_uuid)} className="text-danger">Delete</DropdownItem>
                                                                        </DropdownMenu>
                                                                    </UncontrolledDropdown>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        {/* Pagination for Cities */}
                                        {!loading && cityTotal > cityLimit && (
                                            <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                                                <span className="text-muted small">Showing {((cityPage - 1) * cityLimit) + 1} to {Math.min(cityPage * cityLimit, cityTotal)} of {cityTotal}</span>
                                                <Pagination className="pagination justify-content-end mb-0">
                                                    <PaginationItem disabled={cityPage <= 1}>
                                                        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setCityPage(cityPage - 1); }}><i className="fas fa-angle-left" /></PaginationLink>
                                                    </PaginationItem>
                                                    {renderPaginationItems(cityPage, cityTotal, cityLimit, setCityPage)}
                                                    <PaginationItem disabled={cityPage * cityLimit >= cityTotal}>
                                                        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setCityPage(cityPage + 1); }}><i className="fas fa-angle-right" /></PaginationLink>
                                                    </PaginationItem>
                                                </Pagination>
                                            </CardFooter>
                                        )}
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={modal} toggle={toggle} centered>
                <ModalHeader toggle={toggle} className="border-0">
                    {editingItem ? (editingItem.type === "country" ? "Rename Country" : editingItem.type === "state" ? "Edit State" : "Edit City") : (modalType === "country" ? "Add Operational Country" : modalType === "state" ? "Add State" : "Add New City")}
                </ModalHeader>
                <ModalBody>
                    <form onSubmit={handleSubmit} noValidate>
                        {(modalType === "state" || modalType === "city") && (
                            <FormGroup>
                                <label className="form-control-label">Select Country <span className="text-danger">*</span></label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadCountryOptions}
                                    onChange={(opt) => {
                                        setSelectedCountryOption(opt);
                                        setSelectedStateOption(null); // reset state selection if country changes
                                        if (errors.country) setErrors({ ...errors, country: null });
                                    }}
                                    value={selectedCountryOption}
                                    placeholder="Search country..."
                                    styles={{
                                        ...selectStyles,
                                        control: (base) => ({
                                            ...selectStyles.control(base),
                                            borderColor: errors.country ? '#dc3545' : '#cad1d7'
                                        })
                                    }}
                                    isClearable
                                />
                                {errors.country && <small className="text-danger d-block mt-1">{errors.country}</small>}
                                <small className="text-muted mt-2 d-block">Search and select the target country from our operational list.</small>
                            </FormGroup>
                        )}
                        {modalType === "city" && (
                            <FormGroup>
                                <label className="form-control-label">Select State <span className="text-danger">*</span></label>
                                <AsyncSelect
                                    key={selectedCountryOption?.value || "no-country"}
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadStateOptions}
                                    onChange={(opt) => {
                                        setSelectedStateOption(opt);
                                        if (errors.state) setErrors({ ...errors, state: null });
                                    }}
                                    value={selectedStateOption}
                                    placeholder={selectedCountryOption ? "Search state..." : "Select country first"}
                                    styles={{
                                        ...selectStyles,
                                        control: (base) => ({
                                            ...selectStyles.control(base),
                                            borderColor: errors.state ? '#dc3545' : '#cad1d7'
                                        })
                                    }}
                                    isClearable
                                    disabled={!selectedCountryOption}
                                />
                                {errors.state && <small className="text-danger d-block mt-1">{errors.state}</small>}
                                <small className="text-muted mt-2 d-block">Select the state within the chosen country.</small>
                            </FormGroup>
                        )}
                        <FormGroup>
                            <label className="form-control-label">
                                {modalType === "country" ? "Country Name" : modalType === "state" ? "State Name" : "City Name"} <span className="text-danger">*</span>
                            </label>
                            <Input
                                type="text"
                                placeholder={modalType === "country" ? "e.g. Ethiopia" : modalType === "state" ? "e.g. Amhara" : "e.g. Addis Ababa"}
                                value={newLabel}
                                invalid={!!errors.name}
                                onChange={(e) => {
                                    setNewLabel(e.target.value);
                                    if (errors.name) setErrors({ ...errors, name: null });
                                }}
                                style={{ borderRadius: "8px", minHeight: "45px" }}
                            />
                            {errors.name && <small className="text-danger mt-1">{errors.name}</small>}
                        </FormGroup>
                        {modalType === "country" && (
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <label className="form-control-label">Latitude</label>
                                        <Input
                                            type="number"
                                            step="any"
                                            placeholder="0.0000"
                                            value={latitude}
                                            onChange={(e) => setLatitude(e.target.value)}
                                            style={{ borderRadius: "8px", minHeight: "45px" }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <label className="form-control-label">Longitude</label>
                                        <Input
                                            type="number"
                                            step="any"
                                            placeholder="0.0000"
                                            value={longitude}
                                            onChange={(e) => setLongitude(e.target.value)}
                                            style={{ borderRadius: "8px", minHeight: "45px" }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}
                        <Button color="primary" block type="submit" style={{ minHeight: "45px", borderRadius: "10px" }}>
                            {editingItem ? "Update" : "Save"} {modalType === "country" ? "Country" : modalType === "state" ? "State" : "City"}
                        </Button>
                    </form>
                </ModalBody>
            </Modal>

            {/* View Cities Modal */}
            <Modal isOpen={viewCitiesModal} toggle={toggleViewCitiesModal} size="xl" centered>
                <ModalHeader toggle={toggleViewCitiesModal} className="border-0">
                    <div className="d-flex align-items-center">
                        <i className="fas fa-city mr-3 text-info" style={{ fontSize: "1.5rem" }} />
                        <div>
                            <h4 className="mb-0">Cities in {selectedViewCountry?.c_name}</h4>
                            <small className="text-muted">Total associated urban operational centers</small>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="p-0">
                    <div className="table-responsive">
                        <Table className="align-items-center table-flush" hover>
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ padding: "16px 24px" }}>City Name</th>
                                    <th>Status</th>
                                    <th>Added On</th>
                                    <th className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewCitiesLoading ? (<tr><td colSpan="4" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-info" /></td></tr>) :
                                    countryCities.length === 0 ? (<tr><td colSpan="4" className="text-center py-5 text-muted">No cities recorded.</td></tr>) : (
                                        countryCities.map(city => (
                                            <tr key={city.ci_uuid}>
                                                <td style={{ padding: "16px 24px" }}><span className="font-weight-600 text-dark">{city.ci_name}</span></td>
                                                <td><Badge pill color={city.ci_status === "1" ? "success" : "secondary"}>{city.ci_status === "1" ? "Active" : "Inactive"}</Badge></td>
                                                <td>{new Date(city.createdAt).toLocaleDateString()}</td>
                                                <td className="text-right" style={{ paddingRight: "24px" }}>
                                                    <Button color="info" outline size="sm" onClick={() => { toggleViewCitiesModal(); handleEditCity(city); }}>Edit</Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                            </tbody>
                        </Table>
                    </div>
                    {/* Pagination for Modal Cities */}
                    {!viewCitiesLoading && countryCitiesTotal > 10 && (
                        <div className="p-3 d-flex justify-content-between align-items-center" style={{ ...styles.pagination, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
                            <span className="text-muted small font-weight-bold">Showing {((countryCitiesPage - 1) * 10) + 1} to {Math.min(countryCitiesPage * 10, countryCitiesTotal)} of {countryCitiesTotal}</span>
                            <nav aria-label="...">
                                <Pagination className="pagination justify-content-end mb-0" listClassName="justify-content-end mb-0">
                                    <PaginationItem disabled={countryCitiesPage <= 1}>
                                        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchCountryCities(selectedViewCountry.c_uuid, countryCitiesPage - 1); }}><i className="fas fa-angle-left" /></PaginationLink>
                                    </PaginationItem>
                                    {renderPaginationItems(countryCitiesPage, countryCitiesTotal, 10, (p) => fetchCountryCities(selectedViewCountry.c_uuid, p))}
                                    <PaginationItem disabled={countryCitiesPage * 10 >= countryCitiesTotal}>
                                        <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchCountryCities(selectedViewCountry.c_uuid, countryCitiesPage + 1); }}><i className="fas fa-angle-right" /></PaginationLink>
                                    </PaginationItem>
                                </Pagination>
                            </nav>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* View States Modal */}
            <Modal isOpen={viewStatesModal} toggle={toggleViewStatesModal} size="lg" centered>
                <ModalHeader toggle={toggleViewStatesModal} className="border-0">
                    <div className="d-flex align-items-center">
                        <i className="fas fa-map-marked-alt mr-3 text-primary" style={{ fontSize: "1.5rem" }} />
                        <div>
                            <h4 className="mb-0">States in {selectedViewStatesCountry?.c_name}</h4>
                            <small className="text-muted">Regional operational divisions</small>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="p-0">
                    <div className="table-responsive">
                        <Table className="align-items-center table-flush" hover>
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ padding: "16px 24px" }}>State Name</th>
                                    <th>Total Cities</th>
                                    <th className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewStatesLoading ? (<tr><td colSpan="3" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td></tr>) :
                                    countryStatesData.length === 0 ? (<tr><td colSpan="3" className="text-center py-5 text-muted">No states recorded.</td></tr>) : (
                                        countryStatesData.map(state => (
                                            <tr key={state.s_uuid}>
                                                <td style={{ padding: "16px 24px" }}><span className="font-weight-600 text-dark">{state.s_name}</span></td>
                                                <td><Badge pill color="info">{state.cityCount || 0} Cities</Badge></td>
                                                <td className="text-right" style={{ paddingRight: "24px" }}>
                                                    <Button color="primary" outline size="sm" onClick={() => { toggleViewStatesModal(); handleEditState(state); }} className="mr-2">Edit</Button>
                                                    <Button color="info" outline size="sm" onClick={() => { toggleViewStatesModal(); handleViewStateCities(state); }}>Cities</Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                            </tbody>
                        </Table>
                    </div>
                    {!viewStatesLoading && countryStatesTotal > 10 && (
                        <div className="p-4 d-flex justify-content-between align-items-center border-top bg-light">
                            <span className="text-muted small">Showing {((countryStatesPage - 1) * 10) + 1} to {Math.min(countryStatesPage * 10, countryStatesTotal)} of {countryStatesTotal}</span>
                            <Pagination className="mb-0">
                                <PaginationItem disabled={countryStatesPage <= 1}>
                                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchCountryStates(selectedViewStatesCountry.c_uuid, countryStatesPage - 1); }}><i className="fas fa-angle-left" /></PaginationLink>
                                </PaginationItem>
                                {renderPaginationItems(countryStatesPage, countryStatesTotal, 10, (p) => fetchCountryStates(selectedViewStatesCountry.c_uuid, p))}
                                <PaginationItem disabled={countryStatesPage * 10 >= countryStatesTotal}>
                                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchCountryStates(selectedViewStatesCountry.c_uuid, countryStatesPage + 1); }}><i className="fas fa-angle-right" /></PaginationLink>
                                </PaginationItem>
                            </Pagination>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {/* View State Cities Modal */}
            <Modal isOpen={viewStateCitiesModal} toggle={toggleViewStateCitiesModal} size="lg" centered>
                <ModalHeader toggle={toggleViewStateCitiesModal} className="border-0">
                    <div className="d-flex align-items-center">
                        <i className="fas fa-city mr-3 text-info" style={{ fontSize: "1.5rem" }} />
                        <div>
                            <h4 className="mb-0">Cities in {selectedViewState?.s_name}</h4>
                            <small className="text-muted">Urban centers within this state</small>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="p-0">
                    <div className="table-responsive">
                        <Table className="align-items-center table-flush" hover>
                            <thead className="thead-light">
                                <tr>
                                    <th style={{ padding: "16px 24px" }}>City Name</th>
                                    <th>Status</th>
                                    <th className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewStateCitiesLoading ? (<tr><td colSpan="3" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-info" /></td></tr>) :
                                    stateCitiesData.length === 0 ? (<tr><td colSpan="3" className="text-center py-5 text-muted">No cities recorded.</td></tr>) : (
                                        stateCitiesData.map(city => (
                                            <tr key={city.ci_uuid}>
                                                <td style={{ padding: "16px 24px" }}><span className="font-weight-600 text-dark">{city.ci_name}</span></td>
                                                <td><Badge pill color={city.ci_status === "1" ? "success" : "secondary"}>{city.ci_status === "1" ? "Active" : "Inactive"}</Badge></td>
                                                <td className="text-right" style={{ paddingRight: "24px" }}>
                                                    <Button color="info" outline size="sm" onClick={() => { toggleViewStateCitiesModal(); handleEditCity(city); }}>Edit</Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                            </tbody>
                        </Table>
                    </div>
                    {!viewStateCitiesLoading && stateCitiesTotal > 10 && (
                        <div className="p-4 d-flex justify-content-between align-items-center border-top bg-light">
                            <span className="text-muted small">Showing {((stateCitiesPage - 1) * 10) + 1} to {Math.min(stateCitiesPage * 10, stateCitiesTotal)} of {stateCitiesTotal}</span>
                            <Pagination className="mb-0">
                                <PaginationItem disabled={stateCitiesPage <= 1}>
                                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchStateCities(selectedViewState.s_uuid, stateCitiesPage - 1); }}><i className="fas fa-angle-left" /></PaginationLink>
                                </PaginationItem>
                                {renderPaginationItems(stateCitiesPage, stateCitiesTotal, 10, (p) => fetchStateCities(selectedViewState.s_uuid, p))}
                                <PaginationItem disabled={stateCitiesPage * 10 >= stateCitiesTotal}>
                                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchStateCities(selectedViewState.s_uuid, stateCitiesPage + 1); }}><i className="fas fa-angle-right" /></PaginationLink>
                                </PaginationItem>
                            </Pagination>
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

export default CountryCity;
